/**
 * E07-F07 — global Do-Not-Source list (EER clients): DTOs + the pure paste parser.
 *
 * Spec: docs/superpowers/specs/2026-10-14-global-dns-eer-clients-design.md.
 *
 * The DTOs mirror the future `dns_global_entries` row (Prisma `DnsGlobalEntry`)
 * and move to `@eer/shared` together with the endpoints (E07-F07-T02), so the
 * mock and the real adapter import one contract.
 *
 * `parseDnsPaste` / `previewDnsPaste` are the UI-side preview: one company per
 * line, four line formats. They MIRROR the server parser the add endpoint will
 * run — the server stays the authority on what gets saved; this only shows the
 * admin what will happen before they press «Add N».
 */

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Where an entry came from: pasted by an admin, or pulled from a project's client. */
export type DnsSource = 'manual' | 'project_client';

export interface DnsClientEntry {
  readonly id: string;
  /** Display name of the company. */
  readonly name: string;
  /** Normalized domain (`normalizeDnsDomain`), or null for a name-only entry. */
  readonly domain: string | null;
  /** Unique key: the domain, or `name:<normalized name>` when there is none. */
  readonly matchKey: string;
  readonly source: DnsSource;
  /** `clients.id` for an entry pulled from a project; null for manual ones. */
  readonly clientId: number | null;
  /** Soft delete: false = removed (kept, restorable, never resurrected by a pull). */
  readonly isActive: boolean;
  /** Display name of who added it (null for automatic pulls). */
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** GET /admin/dns-clients */
export interface DnsClientsList {
  readonly entries: readonly DnsClientEntry[];
  readonly activeCount: number;
  readonly removedCount: number;
}

/** One recognized pasted line. */
export interface DnsPasteItem {
  /** 1-based line number in the textarea. */
  readonly line: number;
  readonly raw: string;
  readonly name: string;
  readonly domain: string | null;
  readonly matchKey: string;
}

/** A recognized line that is already covered by the list (or by an earlier line). */
export interface DnsPasteExisting extends DnsPasteItem {
  /** The entry it matches; null when it repeats an earlier line of the same paste. */
  readonly match: { readonly id: string; readonly name: string; readonly isActive: boolean } | null;
}

export interface DnsPasteUnrecognized {
  readonly line: number;
  readonly raw: string;
  readonly reason: string;
}

/** POST /admin/dns-clients/preview */
export interface DnsPastePreview {
  readonly fresh: readonly DnsPasteItem[];
  readonly existing: readonly DnsPasteExisting[];
  readonly unrecognized: readonly DnsPasteUnrecognized[];
}

/**
 * POST /admin/dns-clients/pull-from-projects. Counts are DISTINCT list entries
 * (E07-F07-T06), so they never exceed the Active / Removed tabs.
 */
export interface DnsPullResult {
  /** New entries created from project clients. */
  readonly added: number;
  /** Active entries that already cover a project client. */
  readonly alreadyListed: number;
  /** Removed entries that cover a project client — deliberately NOT brought back. */
  readonly skippedRemoved: number;
}

/** POST /admin/dns-clients/pull-from-projects/preview — saves nothing (E07-F07-T06). */
export interface DnsPullPreview {
  /** Project clients the pull would add (one per company). */
  readonly fresh: readonly {
    readonly clientId: number;
    readonly name: string;
    readonly domain: string | null;
  }[];
  /** Distinct ACTIVE entries that already cover project clients. */
  readonly alreadyListed: readonly DnsPullMatch[];
  /** Distinct REMOVED entries that cover project clients — they stay removed. */
  readonly removed: readonly DnsPullMatch[];
}

export interface DnsPullMatch {
  readonly id: string;
  readonly name: string;
  readonly domain: string | null;
}

/** PATCH /admin/dns-clients/:id */
export interface DnsClientUpdate {
  readonly name: string;
  /** Raw input; normalized on save. Empty → name-only entry. */
  readonly domain: string;
}

/** Hard cap on one paste — the add endpoint rejects bigger bodies. */
export const DNS_PASTE_MAX_LINES = 1000;

// ---------------------------------------------------------------------------
// Normalizers (mirror packages/api researchPipelineStages normalizeDomain /
// normalizeCompanyName — the same keys the research DNS index matches on)
// ---------------------------------------------------------------------------

const CORP_SUFFIXES = new Set([
  'inc',
  'incorporated',
  'llc',
  'ltd',
  'limited',
  'corp',
  'corporation',
  'co',
  'company',
  'gmbh',
  'ag',
  'plc',
  'sa',
  'sas',
  'bv',
  'oy',
  'ab',
  'group',
  'holdings',
  'holding',
]);

/** Host-only, lowercased, no scheme / www / path / port. Null for blank input. */
export function normalizeDnsDomain(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let d = String(raw).trim().toLowerCase();
  if (!d) return null;
  d = d.replace(/^https?:\/\//, '').replace(/^www\./, '');
  d = (d.split(/[/?#\s]/)[0] ?? '').replace(/:\d+$/, '');
  return d || null;
}

const DOMAIN_RE =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

/** True when the text reads as a domain or URL (not a company name). */
export function looksLikeDomain(text: string): boolean {
  const t = text.trim();
  if (t === '' || /\s/.test(t) || t.includes('@')) return false;
  const host = normalizeDnsDomain(t);
  return host !== null && DOMAIN_RE.test(host);
}

/** Lowercase, no diacritics/punctuation, corporate suffixes dropped. */
export function normalizeDnsName(raw: string): string {
  const tokens = raw
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((t) => t && !CORP_SUFFIXES.has(t));
  return tokens.join(' ');
}

/** The uniqueness key: domain, else `name:<normalized name>`. */
export function dnsMatchKey(name: string, domain: string | null): string {
  return domain ?? `name:${normalizeDnsName(name)}`;
}

// ---------------------------------------------------------------------------
// Paste parser
// ---------------------------------------------------------------------------

type LineResult =
  | { readonly kind: 'item'; readonly item: DnsPasteItem }
  | { readonly kind: 'bad'; readonly bad: DnsPasteUnrecognized }
  | { readonly kind: 'blank' };

function item(line: number, raw: string, name: string, domain: string | null): LineResult {
  const cleanName = name.trim().replace(/\s+/g, ' ');
  if (normalizeDnsName(cleanName) === '' && domain === null) {
    return { kind: 'bad', bad: { line, raw, reason: 'No company name or domain' } };
  }
  if (cleanName.length > 120) {
    return { kind: 'bad', bad: { line, raw, reason: 'Name is longer than 120 characters' } };
  }
  return {
    kind: 'item',
    item: { line, raw, name: cleanName, domain, matchKey: dnsMatchKey(cleanName, domain) },
  };
}

/**
 * Parse one pasted line. Formats:
 *   `Name` · `domain.com` / URL · `Name<TAB>domain.com` · `Name, domain.com`.
 * A domain-only line takes the domain as its display name.
 */
export function parseDnsLine(raw: string, line: number): LineResult {
  const text = raw.trim();
  if (text === '') return { kind: 'blank' };
  if (/\S+@\S+\.\S+/.test(text)) {
    return {
      kind: 'bad',
      bad: { line, raw, reason: 'Looks like an email — paste the company domain' },
    };
  }

  // Two Excel columns (TAB). Empty trailing cells are ignored.
  if (text.includes('\t')) {
    const cells = text
      .split('\t')
      .map((c) => c.trim())
      .filter((c) => c !== '');
    if (cells.length === 1) return parseDnsLine(cells[0] ?? '', line);
    if (cells.length > 2) {
      return { kind: 'bad', bad: { line, raw, reason: 'More than two columns' } };
    }
    const [name = '', second = ''] = cells;
    if (!looksLikeDomain(second)) {
      return { kind: 'bad', bad: { line, raw, reason: 'Second column is not a domain' } };
    }
    return item(line, raw, name, normalizeDnsDomain(second));
  }

  // `Name, domain.com` — split on the LAST comma, and only when the right side is
  // a domain: «Analog Devices, Inc.» stays one name.
  const comma = text.lastIndexOf(',');
  if (comma > 0) {
    const right = text.slice(comma + 1).trim();
    if (looksLikeDomain(right))
      return item(line, raw, text.slice(0, comma), normalizeDnsDomain(right));
  }

  if (looksLikeDomain(text)) {
    const domain = normalizeDnsDomain(text);
    return item(line, raw, domain ?? text, domain);
  }
  if (/^(https?:\/\/|www\.)/i.test(text)) {
    return { kind: 'bad', bad: { line, raw, reason: 'Not a valid web address' } };
  }
  return item(line, raw, text, null);
}

/** Split a paste into recognized items + unrecognized lines (blank lines dropped). */
export function parseDnsPaste(text: string): {
  readonly items: DnsPasteItem[];
  readonly unrecognized: DnsPasteUnrecognized[];
} {
  const items: DnsPasteItem[] = [];
  const unrecognized: DnsPasteUnrecognized[] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const r = parseDnsLine(raw, i + 1);
    if (r.kind === 'item') items.push(r.item);
    else if (r.kind === 'bad') unrecognized.push(r.bad);
  });
  return { items, unrecognized };
}

/**
 * Group a paste against the current list: new / already in list / unrecognized.
 * A line matches an entry by `matchKey`; a name-only line also matches an entry
 * with the same normalized name even when that entry has a domain (otherwise
 * «Intel» would be added next to «Intel · intel.com»). Repeats inside the same
 * paste land in `existing` with `match: null`.
 */
export function previewDnsPaste(text: string, entries: readonly DnsClientEntry[]): DnsPastePreview {
  const { items, unrecognized } = parseDnsPaste(text);
  const byKey = new Map(entries.map((e) => [e.matchKey, e] as const));
  const byName = new Map(entries.map((e) => [normalizeDnsName(e.name), e] as const));
  const seen = new Set<string>();
  const fresh: DnsPasteItem[] = [];
  const existing: DnsPasteExisting[] = [];

  for (const it of items) {
    const hit =
      byKey.get(it.matchKey) ??
      (it.domain === null ? byName.get(normalizeDnsName(it.name)) : undefined);
    if (hit) {
      existing.push({ ...it, match: { id: hit.id, name: hit.name, isActive: hit.isActive } });
    } else if (seen.has(it.matchKey)) {
      existing.push({ ...it, match: null });
    } else {
      seen.add(it.matchKey);
      fresh.push(it);
    }
  }
  return { fresh, existing, unrecognized };
}

/** Case-insensitive search over name + domain. */
export function filterDnsEntries(
  entries: readonly DnsClientEntry[],
  opts: { readonly active: boolean; readonly query: string },
): DnsClientEntry[] {
  const q = opts.query.trim().toLowerCase();
  return entries
    .filter((e) => e.isActive === opts.active)
    .filter((e) => q === '' || e.name.toLowerCase().includes(q) || (e.domain ?? '').includes(q))
    .sort((a, b) => a.name.localeCompare(b.name));
}
